/**
 * Fields List Component
 *
 * Shared component for displaying and editing field schemas.
 * Used by both CreateTableSheet (ConfigureStep) and PreviewPanel (SchemaTab).
 */

import { useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Collapsible, CollapsibleContent, Badge, cn } from '@loader/ui';
import { FIELD_DATA_TYPE_LABELS, type FieldDataType } from '@loader/shared-types';

import { getTypeIcon } from '../constants/fieldTypes';

// ============================================================================
// TYPES
// ============================================================================

export interface FieldData {
  id: string;
  name: string;
  dataType: FieldDataType;
  isRequired: boolean;
  isUnique: boolean;
  description?: string | null;
}

export interface FieldsTableProps<T extends FieldData> {
  /** Array of fields to display */
  fields: T[];
  /** Whether the list is disabled */
  disabled?: boolean;
  /** Expandable row content renderer (shows edit button when provided) */
  renderExpanded?: (field: T) => ReactNode;
  /** Empty state message */
  emptyMessage?: string;
}

// ============================================================================
// FIELD ROW COMPONENT
// ============================================================================

interface FieldRowProps<T extends FieldData> {
  field: T;
  disabled?: boolean;
  renderExpanded?: (field: T) => ReactNode;
}

function FieldRow<T extends FieldData>({
  field,
  disabled,
  renderExpanded,
}: FieldRowProps<T>) {
  const [isExpanded, setIsExpanded] = useState(false);
  const TypeIcon = getTypeIcon(field.dataType);
  const isExpandable = !!renderExpanded;
  const typeLabel = FIELD_DATA_TYPE_LABELS[field.dataType] || field.dataType;

  const fieldNameContent = (
    <span className="text-sm font-medium truncate">
      {field.name}
    </span>
  );

  const rowContent = (
    <button
      type="button"
      onClick={() => isExpandable && setIsExpanded(!isExpanded)}
      disabled={disabled || !isExpandable}
      className={cn(
        'flex items-center gap-2 py-2.5 px-3 w-full text-left',
        isExpandable && 'cursor-pointer hover:bg-muted/50',
        isExpanded && 'bg-muted/30',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      {/* Rotating arrow */}
      {isExpandable && (
        <ChevronRight
          className={cn(
            'h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200',
            isExpanded && 'rotate-90'
          )}
        />
      )}

      {/* Field name */}
      <div className="flex-1 min-w-0">
        {fieldNameContent}
      </div>

      {/* Type badge */}
      <Badge variant="secondary" className="shrink-0 gap-1.5 font-normal">
        <TypeIcon className="h-3 w-3" />
        {typeLabel}
      </Badge>
    </button>
  );

  if (!isExpandable) {
    return <div>{rowContent}</div>;
  }

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      {rowContent}
      <CollapsibleContent>
        <div className="px-3 pb-3 pl-9">
          {renderExpanded(field)}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function FieldsTable<T extends FieldData>({
  fields,
  disabled = false,
  renderExpanded,
  emptyMessage = 'No fields defined',
}: FieldsTableProps<T>) {
  if (fields.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="divide-y">
      {fields.map((field) => (
        <FieldRow
          key={field.id}
          field={field}
          disabled={disabled}
          renderExpanded={renderExpanded}
        />
      ))}
    </div>
  );
}
